import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Button } from '@ouropix/ui';
import { Badge } from '@ouropix/ui';
import { ConfirmModal } from '@ouropix/ui';
import {
  Plus, Pencil, Trash2, Palette, Copy, Eye, Loader2,
} from 'lucide-react';
import AdminPageHeader from '@/components/layout/PageHeader';
import { toast } from 'sonner';
import { apiErrorMessage } from '@/lib/api-helpers';
import { cn } from '@/lib/utils';
import type { GameTheme } from '@/components/games/types';
import { MODALITIES, getModality, type ModalityConfig } from '@/components/temas/modalities';
import { ThemeEditor } from '@/components/temas/ThemeEditor';
import { ThemeThumbnail } from '@/components/temas/ThemeThumbnail';
import { DesignKitGenerator } from '@/components/temas/DesignKitGenerator';

export const Route = createFileRoute('/_admin/temas/')({
  component: TemasPage,
});

function mapApiTheme(t: any): GameTheme {
  return {
    id: t.id,
    name: t.name ?? '',
    code: t.modalityCode ?? t.modality_code ?? t.code ?? '',
    description: t.description ?? null,
    primary_color: t.accentColor ?? t.accent_color ?? t.primary_color ?? null,
    secondary_color: t.secondaryColor ?? t.secondary_color ?? null,
    background_color: t.backgroundColor ?? t.background_color ?? null,
    preview_image_url: t.previewScreenshotUrl ?? t.preview_screenshot_url ?? t.coverImageUrl ?? t.cover_image_url ?? null,
    active: t.active ?? true,
    created_at: t.createdAt ?? t.created_at ?? '',
    modality_code: t.modalityCode ?? t.modality_code ?? null,
    theme_assets: (t.themeAssets ?? t.theme_assets ?? null) as Record<string, string> | null,
    preview_screenshot_url: t.previewScreenshotUrl ?? t.preview_screenshot_url ?? null,
    cover_image_url: t.coverImageUrl ?? t.cover_image_url ?? null,
    banner_image_url: t.bannerImageUrl ?? t.banner_image_url ?? null,
    avatar_image_url: t.avatarImageUrl ?? t.avatar_image_url ?? null,
  };
}

function ThemeCard({
  theme,
  modality,
  onEdit,
  onDelete,
  onDuplicate,
  onPreview,
}: {
  theme: GameTheme;
  modality: ModalityConfig;
  onEdit: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onPreview: () => void;
}) {
  const themeAssets = (theme.theme_assets as Record<string, string>) ?? {};

  return (
    <div className="rounded-lg border bg-card overflow-hidden group hover:border-primary/50 transition-colors flex flex-col">
      {/* Portrait thumbnail — aspect-ratio 9/16, SVG preview */}
      <div className="relative w-full overflow-hidden bg-muted" style={{ aspectRatio: '390/700' }}>
        {theme.preview_screenshot_url ? (
          <img
            src={theme.preview_screenshot_url}
            alt={`Preview ${theme.name}`}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <ThemeThumbnail modality={modality} assets={themeAssets} />
        )}
        <Badge
          variant={theme.active ? 'default' : 'secondary'}
          className="absolute top-2 right-2 shadow-md"
        >
          {theme.active ? 'Ativo' : 'Inativo'}
        </Badge>
      </div>

      <div className="p-3 flex flex-col gap-2 flex-1">
        <div>
          <h3 className="font-semibold text-sm truncate">{theme.name}</h3>
          {theme.description && (
            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{theme.description}</p>
          )}
        </div>

        {/* Color swatches */}
        <div className="flex gap-1.5 flex-wrap">
          {modality.assets
            .filter((a) => a.isColor && themeAssets[a.key])
            .slice(0, 5)
            .map((a) => (
              <div
                key={a.key}
                title={`${a.label}: ${themeAssets[a.key]}`}
                className="w-4 h-4 rounded-full border border-border/50 shadow-sm"
                style={{ backgroundColor: themeAssets[a.key] }}
              />
            ))}
        </div>

        <div className="flex items-center justify-between mt-auto gap-2">
          <Button size="sm" variant="outline" className="h-8 flex-1" onClick={onEdit}>
            <Pencil className="h-3 w-3 mr-1" />
            Editar
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-8 w-8 p-0"
            onClick={onPreview}
            title="Pré-visualizar"
          >
            <Eye className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-8 w-8 p-0"
            onClick={onDuplicate}
            title="Duplicar tema"
          >
            <Copy className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-8 w-8 p-0 text-destructive hover:text-destructive"
            onClick={onDelete}
            title="Excluir tema"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function TemasPage() {
  const queryClient = useQueryClient();
  const [activeModality, setActiveModality] = useState<string>(MODALITIES[0].code);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingTheme, setEditingTheme] = useState<GameTheme | null>(null);
  const [themeToDelete, setThemeToDelete] = useState<GameTheme | null>(null);

  const { data: themes = [], isLoading } = useQuery<GameTheme[]>({
    queryKey: ['admin-game-themes'],
    queryFn: async () => {
      const res = await api.get('/admin/game-themes');
      const raw = res.data?.themes ?? res.data?.data ?? res.data ?? [];
      return (Array.isArray(raw) ? raw : []).map(mapApiTheme);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/admin/game-themes/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-game-themes'] });
      toast.success('Tema excluído!');
      setThemeToDelete(null);
    },
    onError: (err) => toast.error(apiErrorMessage(err, 'Erro ao excluir tema')),
  });

  const duplicateMutation = useMutation({
    mutationFn: (theme: GameTheme) =>
      api.post('/admin/game-themes', {
        name: `${theme.name} (Cópia)`,
        description: theme.description,
        modality_code: theme.modality_code,
        theme_assets: theme.theme_assets,
        background_color: theme.background_color,
        primary_color: theme.primary_color,
        active: false,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-game-themes'] });
      toast.success('Tema duplicado!');
    },
    onError: (err) => toast.error(apiErrorMessage(err, 'Erro ao duplicar tema')),
  });

  const handleNew = () => {
    setEditingTheme(null);
    setEditorOpen(true);
  };

  const handleEdit = (theme: GameTheme) => {
    setEditingTheme(theme);
    setEditorOpen(true);
  };

  const handlePreview = (theme: GameTheme) => {
    const code = theme.modality_code || theme.code;
    if (code) {
      window.open(`/admin/temas/preview/${code}?theme_id=${theme.id}`, '_blank');
    } else {
      toast.info('Preview disponível apenas para temas vinculados a uma modalidade');
    }
  };

  const getThemesForModality = (code: string) =>
    themes.filter((t) => (t.modality_code || t.code) === code);

  const currentModality = getModality(activeModality)!;
  const modalityThemes = getThemesForModality(activeModality);

  return (
    <div className="space-y-6">
      <AdminPageHeader
        icon={Palette}
        title="Temas de Jogos"
        description="Gerencie temas visuais por modalidade de jogo"
      />

      {/* Mobile dropdown */}
      <div className="sm:hidden">
        <select
          value={activeModality}
          onChange={(e) => setActiveModality(e.target.value)}
          className="w-full rounded-lg border bg-card px-3 py-2 text-sm text-foreground"
        >
          {MODALITIES.map((m) => (
            <option key={m.code} value={m.code}>
              {m.label}
            </option>
          ))}
        </select>
      </div>

      {/* Sidebar + content */}
      <div className="flex gap-0 rounded-lg border bg-card overflow-hidden min-h-[500px]">
        <nav className="hidden sm:flex w-56 shrink-0 border-r bg-muted/30 p-2 flex-col gap-1">
          {MODALITIES.map((m) => {
            const count = getThemesForModality(m.code).length;
            const isActive = activeModality === m.code;
            return (
              <button
                key={m.code}
                type="button"
                onClick={() => setActiveModality(m.code)}
                className={cn(
                  'flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium w-full text-left transition-colors',
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                )}
              >
                <span className="flex-1 truncate">{m.label}</span>
                {count > 0 && (
                  <span
                    className={cn(
                      'text-[10px] font-semibold px-1.5 py-0.5 rounded-full min-w-[20px] text-center leading-none',
                      isActive
                        ? 'bg-primary-foreground/20 text-primary-foreground'
                        : 'bg-background text-muted-foreground',
                    )}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        <div className="flex-1 p-5 min-w-0">
          <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
            <h2 className="text-base font-semibold truncate">
              Temas — {currentModality.label}
            </h2>
            <div className="flex items-center gap-2">
              <DesignKitGenerator
                modality={currentModality}
                assets={{}}
                label="Baixar Kit de Design"
                size="sm"
              />
              <Button size="sm" onClick={handleNew}>
                <Plus className="h-4 w-4 mr-1" />
                Novo Tema
              </Button>
            </div>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : modalityThemes.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground">
              <Palette className="h-10 w-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">Nenhum tema para esta modalidade.</p>
              <Button variant="outline" size="sm" className="mt-4" onClick={handleNew}>
                <Plus className="h-4 w-4 mr-1" />
                Criar Primeiro Tema
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {modalityThemes.map((theme) => (
                <ThemeCard
                  key={theme.id}
                  theme={theme}
                  modality={currentModality}
                  onEdit={() => handleEdit(theme)}
                  onDelete={() => setThemeToDelete(theme)}
                  onDuplicate={() => duplicateMutation.mutate(theme)}
                  onPreview={() => handlePreview(theme)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <ThemeEditor
        open={editorOpen}
        onOpenChange={setEditorOpen}
        modality={currentModality}
        theme={editingTheme}
      />

      <ConfirmModal
        isOpen={!!themeToDelete}
        onClose={() => setThemeToDelete(null)}
        onConfirm={() => {
          if (themeToDelete) deleteMutation.mutate(themeToDelete.id);
        }}
        title="Excluir tema?"
        description={`Tem certeza que deseja excluir o tema "${themeToDelete?.name}"? Esta ação é irreversível.`}
        confirmText="Excluir"
        cancelText="Cancelar"
        variant="destructive"
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}
